import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CreditCard, AlertCircle, TrendingUp } from 'lucide-react';
import { openRazorpayCheckout } from '../../utils/razorpayCheckout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

const CreditSummaryCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/agent/credit/balance`, {
          withCredentials: true,
        });
        setData(r.data);
      } catch (e) {
        console.error('credit balance fetch', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handlePayNow = async () => {
    setPaying(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/agent/credit/settle-overdue`,
        {},
        { withCredentials: true },
      );
      const d = r.data;
      if (!d?.success) {
        alert('Unable to initiate payment.');
        return;
      }
      await openRazorpayCheckout({
        createOrderPayload: {
          context: 'credit',
          receipt: d.receipt || d.order_id,
          amount: Number(d.amount),
          agent_id: d.agent_id,
          metadata: { agent_id: d.agent_id },
        },
        prefill: d.prefill || {},
        description: 'Credit line settlement',
        onVerified: (verify) => {
          if (verify.redirect_url) {
            window.location.href = verify.redirect_url;
          } else {
            window.location.reload();
          }
        },
      });
    } catch (e) {
      if (e?.message !== 'Payment cancelled') {
        const detail = e.response?.data?.detail;
        alert(detail?.message || detail || e.message || 'Payment init failed');
      }
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 animate-pulse h-32" />
    );
  }
  if (!data) return null;

  const used = Number(data.credit_used || 0);
  const limit = Number(data.credit_limit || 0);
  const available = Number(data.credit_available || 0);
  const overdue = Number(data.overdue_amount || 0);
  const utilPct = limit > 0 ? Math.round((used / limit) * 100) : 0;

  return (
    <div
      className="bg-gradient-to-br from-[#1B375B] to-[#2d4d7a] text-white rounded-2xl p-6 shadow-lg"
      data-testid="agent-credit-summary"
    >
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-white/20 rounded-xl">
            <CreditCard size={28} />
          </div>
          <div>
            <p className="text-lg font-semibold">Credit Facility</p>
            <p className="text-sm text-white/70">
              {data.credit_status === 'ACTIVE' ? 'Active' : 'Suspended'} · Pay day: {data.payment_day || '-'}
            </p>
          </div>
        </div>
        {used > 0 && (
          <button
            onClick={handlePayNow}
            disabled={paying}
            data-testid="agent-pay-outstanding-btn"
            className="px-4 py-2 bg-white text-[#1B375B] rounded-lg font-semibold hover:bg-white/90 disabled:opacity-50 flex items-center gap-2"
          >
            {paying ? 'Opening payment…' : `Pay ${inr(Math.max(overdue, used))}`}
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div>
          <p className="text-xs text-white/70 mb-1">Credit Limit</p>
          <p className="text-2xl font-bold">{inr(limit)}</p>
        </div>
        <div>
          <p className="text-xs text-white/70 mb-1">Utilized</p>
          <p className="text-2xl font-bold text-amber-200">{inr(used)}</p>
          <p className="text-[11px] text-white/60">{utilPct}% used</p>
        </div>
        <div>
          <p className="text-xs text-white/70 mb-1">Available</p>
          <p className="text-2xl font-bold text-green-200">{inr(available)}</p>
        </div>
      </div>

      <div className="mt-4 h-2 bg-white/15 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all ${utilPct >= 90 ? 'bg-red-400' : utilPct >= 70 ? 'bg-amber-300' : 'bg-green-300'}`}
          style={{ width: `${Math.min(100, utilPct)}%` }}
        />
      </div>

      {overdue > 0 && (
        <div className="mt-4 flex items-center gap-2 p-3 bg-red-500/30 rounded-lg border border-red-300/40">
          <AlertCircle size={18} />
          <p className="text-sm">
            <strong>Overdue:</strong> {inr(overdue)} · Next due: {data.next_due_date || 'N/A'}
          </p>
        </div>
      )}
      {data.credit_status === 'SUSPENDED' && (
        <div className="mt-4 flex items-center gap-2 p-3 bg-amber-500/30 rounded-lg border border-amber-300/40">
          <TrendingUp size={18} />
          <p className="text-sm">Credit facility is currently suspended. Settle outstanding amount to reactivate.</p>
        </div>
      )}
    </div>
  );
};

export default CreditSummaryCard;
